<template>
  <div
    v-tooltip="eventTooltip"
    class="member-event"
    :class="{ 'a-tooltip': showImportedTimeOffBadge }"
    :aria-label="hrisImportedEventAriaLabel"
    :style="style"
    :data-testid="showImportedTimeOffBadge ? 'schedules-time-off-event' : null"
  >
    <div class="fc-content">
      <div class="fc-title d-flex align-items-center justify-content-between a-tooltip member-view-container">
        <div class="d-flex align-items-center min-w-0 flex-grow-1">
          <TimeOffHrisImportedBadge
            v-if="showImportedTimeOffBadge"
            :integration-type="importedIntegrationType"
            class="mr-5"
          />
          <span class="member-view-container-title">{{ title }}</span>
        </div>
        <span
          v-if="isShift && canViewStatus"
          class="fc-shift-status member-view-container-status"
        >
          {{ status }}
        </span>
        <span
          v-if="!isShift"
          class="fc-shift-status member-view-container-status"
        >
          {{ duration }}
        </span>
      </div>
      <div class="d-flex justify-content-between">
        <div class="fc-time-range">{{ range }}</div>

        <div
          v-if="shiftSpillover"
          class="d-flex align-items-center"
        >
          <i
            v-tooltip="{ placement: 'top' }"
            class="hi-right"
            title="This shift continues into the next day"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { formatDatetimeRange } from '~/utils/time_utils';
import tooltipDirective from '~/directives/tooltip';
import SchedulesEventMixin from '~/mixins/attendance_schedules/SchedulesEventMixin';
import TimeOffHrisImportedBadge from '~/components/time_off/TimeOffHrisImportedBadge.vue';

// Rendered inside @fullcalendar/vue3's mini `createApp`, which doesn't inherit the host app's
// context — no `$page` (mapInertiaProps would crash) and no global directives. `timeZone` comes in
// as a prop from the host and the tooltip directive is registered locally.
export default {
  name: 'MembersViewEvent',
  components: { TimeOffHrisImportedBadge },
  directives: { tooltip: tooltipDirective },
  mixins: [SchedulesEventMixin],
  props: {
    event: {
      type: Object,
      required: true,
    },
    timeZone: {
      type: String,
      default: '',
    },
  },
  computed: {
    eventExtendedProps() {
      return this.event.extendedProps;
    },
    type() {
      return this.eventExtendedProps.type;
    },
    status() {
      return this.eventExtendedProps.shiftStatus;
    },
    timePeriod() {
      const {
        startStr,
        extendedProps: { realEnd },
      } = this.event;

      return formatDatetimeRange(moment(startStr), moment(realEnd), { style: 'time', timezone: this.timeZone });
    },
    formattedRange() {
      const { start, end } = this.eventExtendedProps;

      return formatDatetimeRange(moment(start).startOf('day'), moment(end).endOf('day'), { style: 'time' });
    },
    eventProps() {
      const { type, range } = this.eventExtendedProps;
      const { allDay, title } = this.event;

      return {
        Shift: {
          duration: '',
          range: this.timePeriod,
          title: type,
        },
        Holiday: {
          duration: 'All day',
          range: this.formattedRange,
          title: this.event.title,
        },
        'Time off request': {
          duration: allDay ? 'All day' : '',
          range,
          title,
        },
        Job: {
          duration: '',
          range: this.timePeriod,
          title,
        },
      };
    },
    duration() {
      const { type } = this.eventExtendedProps;

      return this.eventProps[type].duration;
    },
    style() {
      const { backgroundColor, borderColor, textColor } = this.event;

      if (this.event.classNames.includes('is-not-approved')) {
        return { color: backgroundColor === '#FFE5C6' ? textColor : '#3c495a' };
      }

      return { backgroundColor, borderColor, color: textColor };
    },
    title() {
      const { type } = this.eventExtendedProps;

      return this.eventProps[type].title;
    },
    range() {
      const { type } = this.eventExtendedProps;

      return this.eventProps[type].range;
    },
    canViewStatus() {
      return this.eventExtendedProps.canViewStatus;
    },
    isShift() {
      return this.type === 'Shift';
    },
  },
};
</script>
